import React, { useState } from "react";
import { Button } from "components/ui/button";
import { Card, CardContent, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Spinner } from "components/ui/spinner";
import { Icon } from "components/icons/Icon";
import { useToast } from "components/ui/use-toast";
import { auth } from "services/api";
import { ButtonLink } from "components/ui/button-link";

export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (email.trim() === "" || isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      await auth.requestPasswordReset(email.trim());
      setRequestSent(true);
      addToast({
        title: "Check your email",
        description: "If an account exists for that address, you'll receive a reset link shortly.",
      });
    } catch (error: unknown) {
      const apiError = (error as { response?: { data?: { detail?: string; error?: string } } })?.response?.data;
      const message = apiError?.detail || apiError?.error || "We couldn't start the reset process. Please try again.";
      addToast({
        variant: "destructive",
        title: "Request failed",
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen w-full flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <Card maxWidth="narrow">
        <div className="flex justify-center">
          <Icon name="logo" size={64} />
        </div>
        <CardTitle
          centered={true}
          title={
            <p className="leading-tight">
              Forgot Password
            </p>
          }
        />

        <CardContent variant="form" className="pt-4">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <p className="text-sm text-center text-slate-600">
              Enter the email associated with your account. If it matches our records we'll send you a reset link.
            </p>
            <Input
              size="sm"
              type="email"
              placeholder="name@example.com"
              value={email}
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              required
              disabled={isLoading || requestSent}
            />
            <Button type="submit" variant="default" disabled={isLoading || email.trim() === "" || requestSent}>
              {isLoading ? <Spinner size="sm" /> : "Send reset link"}
            </Button>
            <div className="text-center text-sm text-slate-600">
              <span>Remembered it?</span>{" "}
              <ButtonLink to="/login">Back to sign in</ButtonLink>
            </div>
            {requestSent && (
              <div className="rounded-md bg-slate-100 px-3 py-2 text-center text-sm text-slate-700">
                We'll email you a reset link if the address is in our system. The link expires after a short time for security.
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </main>
  );
};
